import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Box } from '@mui/material';
import { PsychiatryNoteSection } from '../PsychiatryNoteSection.component';
import { ServerForm, ServerFormFormik } from '../../../../ServerForm';
import { PsychiatryNoteSectionKey, patchSessionPsychiatryNote } from '@uplift/api';
import { useSessionPsychiatryNoteQuery } from '@uplift/queries';
import camelCase from 'lodash/camelCase';
import { SessionTimeSection } from '../../sections/SessionTimeSection.component';
import { AssessmentEditSection } from '../../sections/AssessmentSection.component';
import { CurrentMedicationEditSection } from '../../sections/MedicationSection.component';
import { AllergiesEditSection } from '../../sections/AllergiesSection.component';


export const PsychiatryNoteLiveSectionFormik: React.VFC<{
  title?: string;
  subtitle?: string;
  sessionId: string;
  namespace: PsychiatryNoteSectionKey;
}> = ({ title, subtitle, namespace, sessionId }) => {
  const { data } = useSessionPsychiatryNoteQuery(sessionId, { suspense: true, refetchOnMount: true, keepPreviousData: false });
  const fields = data?.forms?.[camelCase(namespace) as keyof typeof data.forms]?.fields;

  return (
    <PsychiatryNoteSection title={title} subtitle={subtitle}>
      <ServerFormFormik
        enableReinitialize
        fields={fields ?? []}
        onSubmit={async (values) => {
          await patchSessionPsychiatryNote(sessionId, { forms: { [namespace]: values } });
        }}
      >
        <ServerForm fields={fields ?? []} autoSave />
      </ServerFormFormik>
    </PsychiatryNoteSection>
  );
};


export type PsychiatryNoteLiveFormProps = {
  sessionId: string;
  patientId: string;
};


export const PsychiatryNoteLiveForm: React.VFC<PsychiatryNoteLiveFormProps> = ({ sessionId, patientId }) => {
  const { data } = useSessionPsychiatryNoteQuery(sessionId, { suspense: true, cacheTime: 0, staleTime: 0 });

  return (
    <Box>
      <SessionTimeSection sessionId={sessionId} />

      <PsychiatryNoteLiveSectionFormik namespace="history_of_presenting_illness" sessionId={sessionId} title="History of Presenting Illness" />

      <PsychiatryNoteLiveSectionFormik namespace="psychiatric_review_of_system" sessionId={sessionId} title="Psychiatric Review of Systems" />

      {data?.isInitialSession && (
        <PsychiatryNoteLiveSectionFormik
          title="Psychiatric History"
          subtitle="Check all that apply and add details"
          sessionId={sessionId}
          namespace="psychiatric_history"
        />
      )}

      {data?.isInitialSession && (
        <PsychiatryNoteLiveSectionFormik
          title="Social History"
          subtitle="Check all that apply and add details"
          sessionId={sessionId}
          namespace="social_history"
        />
      )}

      {data?.isInitialSession && (
        <PsychiatryNoteLiveSectionFormik
          title="Medical History"
          subtitle="Check all that apply and add details"
          sessionId={sessionId}
          namespace="medical_history"
        />
      )}

      {data?.isInitialSession && (
        <ErrorBoundary fallbackRender={() => null}>
          <React.Suspense fallback={null}>
            <AllergiesEditSection patientId={patientId} />
          </React.Suspense>
        </ErrorBoundary>
      )}
      {data?.isInitialSession && (
        <ErrorBoundary fallbackRender={() => null}>
          <React.Suspense fallback={null}>
            <CurrentMedicationEditSection patientId={patientId} />
          </React.Suspense>
        </ErrorBoundary>
      )}

      <PsychiatryNoteLiveSectionFormik
        title="MSE"
        sessionId={sessionId}
        namespace="mental_status_exam"
      />

      <PsychiatryNoteLiveSectionFormik
        title="Risk Assessment"
        sessionId={sessionId}
        namespace="risk_assessment"
      />

      <PsychiatryNoteLiveSectionFormik
        title="Physical Review of System"
        sessionId={sessionId}
        namespace="physical_review_of_systems"
      />

      <AssessmentEditSection sessionId={sessionId} autoSave />

      <PsychiatryNoteLiveSectionFormik
        title="Diagnosis"
        sessionId={sessionId}
        namespace="diagnosis"
      />

      <PsychiatryNoteLiveSectionFormik
        title="Plan"
        sessionId={sessionId}
        namespace="plan"
      />

      <PsychiatryNoteLiveSectionFormik
        title="Psychotherapy, Evaluation, Crisis Services"
        sessionId={sessionId}
        namespace="psychotherapy_evaluation_crisis_services"
      />

      <PsychiatryNoteLiveSectionFormik
        title="Treatment Education"
        sessionId={sessionId}
        namespace="treatment_education"
      />

      <PsychiatryNoteLiveSectionFormik
        title="Prescribe/Refill"
        sessionId={sessionId}
        namespace="prescribe_refill"
      />

      <PsychiatryNoteLiveSectionFormik
        title="Additional Information"
        sessionId={sessionId}
        namespace="additional_information"
      />
    </Box>
  );
};
